// Guida della pagina Routing AI: popup aperto dal pulsante "Guida" in alto,
// con indice a sinistra e spiegazione dettagliata di ogni parte (provider,
// embedder, pipeline, voce, catalogo, cosa vale per tenant e per piattaforma).
// I testi descrivono il comportamento reale del backend: aggiornarli insieme
// al codice (docs/runbooks/ai-routing-dashboard.md).
import { useState } from 'react';
import {
  BookOpen,
  Brain,
  Building2,
  Database,
  FileSearch,
  Layers,
  LayoutList,
  MessageSquare,
  Mic,
  Plug,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';

interface GuideBlock {
  heading?: string;
  text?: string;
  bullets?: ReadonlyArray<string>;
}

interface GuideSection {
  id: string;
  title: string;
  icon: LucideIcon;
  blocks: ReadonlyArray<GuideBlock>;
}

export const ROUTING_GUIDE_SECTIONS: ReadonlyArray<GuideSection> = [
  {
    id: 'overview',
    title: 'Come è fatta la pagina',
    icon: LayoutList,
    blocks: [
      { text: 'Questa pagina decide quale provider e quale modello eseguono ogni funzione AI del tuo tenant: lettura dei documenti, risposte dalla Knowledge Base, voce, analisi.' },
      {
        heading: 'Ordine di lettura',
        bullets: [
          'Provider: chi è disponibile e quante funzioni usa ciascuno.',
          'Passo 1 · Embedder: la base della ricerca nei documenti. Si sceglie una volta sola e configura in automatico tutto il resto. La sezione è chiusa all\'apertura: un clic sul titolo la apre.',
          'Passo 2 · Funzioni AI: una sezione per fase (caricamento dei documenti, risposte alle domande, ricerca avanzata e memoria, voce, analisi, altre funzioni). Ogni sezione si apre con un clic.',
          'Strumenti avanzati: catalogo dei modelli e registro dei consigli automatici.',
        ],
      },
      {
        heading: 'Come leggere ogni funzione',
        text: 'Ogni riga ha provider e modello. "Come funziona" spiega sempre le stesse cose: cosa fa, quando interviene, impatto e costi, consiglio.',
      },
      {
        heading: 'Agenti',
        text: 'Il modello scelto nel modale di un agente vince su questa pagina. La riga "Agenti AI" in Altre funzioni è solo il modello iniziale proposto ai nuovi agenti.',
      },
    ],
  },
  {
    id: 'providers',
    title: 'Provider e Custom AI',
    icon: Plug,
    blocks: [
      { text: 'I provider nativi sono OpenAI, Claude e Gemini. Le chiavi si inseriscono nella scheda Provider AI; le card in cima mostrano lo stato e quante funzioni della pagina usano ciascun provider.' },
      {
        heading: 'Custom AI',
        text: 'Endpoint compatibili con le API OpenAI: OpenRouter, Ollama, vLLM, RunPod, LM Studio, Together, Groq, Mistral. Si aggiungono dalla sotto-scheda Custom AI della scheda Provider AI. Solo indirizzi HTTPS pubblici: niente host locali o di rete interna.',
      },
      {
        heading: 'Capacità di un endpoint',
        text: 'Dopo l\'aggiunta, ogni modello viene provato: si leggono le capacità dichiarate dal provider (testo, immagini, audio, strumenti, JSON) e si fanno chiamate di prova. Solo le capacità confermate rendono il modello selezionabile.',
      },
      {
        heading: 'Dove si può usare un Custom AI',
        bullets: [
          'Sì: analisi delle conversazioni, analisi e riassunto dei file, OCR e lettura dei documenti, agenti, Landing Builder, annunci IVR, trascrizione di voicemail, video e meeting.',
          'No: sintesi, scomposizione, rerank, riformulazione e contesto dei frammenti della pipeline RAG, Workflow Builder, agente Incentivazione e transcript degli agenti Realtime, che usano solo i provider nativi.',
        ],
      },
    ],
  },
  {
    id: 'embedder',
    title: 'Embedder e configurazione automatica',
    icon: Database,
    blocks: [
      { text: 'L\'embedder trasforma testi (e con Gemini anche immagini e media) in vettori numerici: è ciò che permette di cercare nei documenti per significato e non solo per parole.' },
      {
        heading: 'OpenAI o Gemini',
        bullets: [
          'OpenAI: vettori da 1536 dimensioni, solo testo. Le immagini e i PDF scansionati passano dall\'OCR.',
          'Gemini: vettori da 3072 dimensioni, multimodale: immagini, pagine PDF, audio e video brevi entrano direttamente nella ricerca. Limiti per file: immagini 20 MB, audio 180 s, video 81 s, PDF 50 MB.',
        ],
      },
      {
        heading: 'Scelta definitiva',
        text: 'Cambiare embedder richiede di reindicizzare tutta la Knowledge Base, perché i vettori di dimensioni diverse non sono confrontabili. Per questo la scelta avviene una volta sola, dal popup del passo 1.',
      },
      {
        heading: 'Configurazione consigliata',
        text: 'Alla scelta dell\'embedder ogni funzione riceve il modello migliore del catalogo per quel provider, tra quelli con le capacità richieste. Se il provider non ha un modello adatto (per esempio la voce con Gemini) si usa l\'altro provider o il default di piattaforma.',
      },
      {
        heading: 'Modello chat della pipeline RAG',
        text: 'Il modello scelto nel passo 1 è quello usato da sintesi, scomposizione, riformulazione e contesto dei frammenti finché non scegli esplicitamente un altro modello nella riga della funzione. Le scelte esplicite restano tue: la configurazione consigliata non le sovrascrive.',
      },
      {
        heading: 'Override',
        text: 'Puoi cambiare provider e modello di una funzione, anche verso un altro provider. Sono accettati solo modelli con le capacità che la funzione richiede (per esempio la visione per la sintesi multimodale).',
      },
    ],
  },
  {
    id: 'rag-pipeline',
    title: 'Come funziona la pipeline RAG',
    icon: BookOpen,
    blocks: [
      { text: 'Quando carichi un documento nella Knowledge Base, il testo viene spezzato in pezzi (chunk) di due dimensioni: piccoli per cercarli con precisione, grandi per dare contesto sufficiente all\'agente quando risponde. È la struttura standard delle Knowledge Base enterprise moderne.' },
      {
        heading: 'Chunk figli: la ricerca precisa',
        text: 'Pezzi piccoli, focalizzati su un singolo concetto. Sono quelli cercati semanticamente quando l\'utente fa una domanda: piccoli e mirati per massimizzare la precisione del match.',
      },
      {
        heading: 'Chunk padri: il contesto per l\'agente',
        text: 'Pezzi più ampi (paragrafo o sezione intera) recuperati a partire dal chunk figlio pertinente. È ciò che l\'agente legge davvero per rispondere: evita di tagliare il ragionamento e le risposte tronche.',
      },
      {
        heading: 'Overlap: continuità tra chunk adiacenti',
        text: 'Ogni chunk include una piccola coda del chunk precedente, così nessuna frase viene tagliata a metà al confine tra due pezzi e il filo del discorso resta intero quando un concetto attraversa due chunk consecutivi. I valori di overlap sono scelte tecniche allineate alle best practice di settore e non si modificano da dashboard.',
      },
      {
        heading: 'Contextual Chunking: prefisso di contesto generato da un modello',
        text: 'Prima di vettorializzare ogni chunk, un modello legge titolo del documento, posizione e frammento e scrive un breve prefisso ("questo pezzo parla di X nel documento Y") allegato SOLO al testo dato all\'embedder; il chunk visibile resta invariato. La ricerca semantica è più accurata perché ogni chunk porta con sé il proprio contesto. Usa sempre il modello chat della pipeline RAG scelto nel passo 1. Costo: una chiamata in più per chunk al caricamento, nessun impatto sulla latenza delle domande.',
      },
      {
        heading: 'Testo o multimodale',
        bullets: [
          'Embedder Gemini: immagini, audio e video vengono indicizzati nativamente oltre al testo; la ricerca trova media pertinenti a partire da una domanda testuale.',
          'Embedder OpenAI: pipeline solo testuale; le immagini e i PDF scansionati passano dall\'OCR.',
        ],
      },
    ],
  },
  {
    id: 'ingestion',
    title: 'Caricamento dei documenti',
    icon: FileSearch,
    blocks: [
      { text: 'Cosa succede quando carichi un documento nella Knowledge Base.' },
      {
        bullets: [
          'Parser documenti di acquisto: estrae testata e righe da DDT, fatture e ordini (PDF, immagini, Excel, Word, CSV, HTML).',
          'OCR: legge PDF scansionati e immagini. Con embedder Gemini non serve un passaggio separato: le pagine entrano già come immagini. Spegnere l\'OCR esclude dalla ricerca i PDF scansionati.',
          'Timeout OCR: tempo massimo per documento, da 5 a 60 minuti.',
          'Frammenti: ogni documento viene diviso in frammenti piccoli per la ricerca e sezioni più ampie (padre) da leggere quando un frammento è pertinente.',
          'Contesto dei frammenti: per ogni frammento un modello scrive una breve frase che lo colloca nel documento, così la ricerca è più precisa. Usa il modello chat della pipeline RAG.',
        ],
      },
    ],
  },
  {
    id: 'retrieval',
    title: 'Risposte alle domande',
    icon: MessageSquare,
    blocks: [
      { text: 'Cosa succede quando un utente fa una domanda a un agente che usa la Knowledge Base.' },
      {
        bullets: [
          'Scomposizione: una domanda composta viene divisa in domande più semplici, cercate separatamente.',
          'Ricerca: unisce ricerca per parole e per significato.',
          'Rerank: riordina i passaggi trovati per pertinenza reale. Modello singolo oppure cross-check, con due provider diversi e classifiche fuse.',
          'Sintesi: scrive la risposta con le citazioni delle fonti. Interviene quando l\'agente non usa la propria sintesi nativa.',
          'Sintesi multimodale: quando tra i passaggi ci sono immagini, un modello con visione le guarda davvero. Richiede l\'embedder Gemini; ha un fallback opzionale e si può spegnere.',
        ],
      },
    ],
  },
  {
    id: 'rag-plus',
    title: 'Ricerca avanzata e memoria',
    icon: Sparkles,
    blocks: [
      {
        heading: 'Ricerca avanzata (RAG Plus)',
        text: 'Quattro tecniche attivate insieme, attive per impostazione predefinita: riformulazione della domanda, risposta ipotetica, paragrafo intero attorno al passaggio trovato, quantità adattiva di passaggi (da 5 a 25). Costa due brevi chiamate in più per domanda e vale per tutti gli agenti del tenant, voce compresa.',
      },
      {
        heading: 'Documenti caricati prima',
        text: 'Restano ricercabili come prima. Il paragrafo intero vale per i documenti caricati con la ricerca avanzata attiva: per estenderlo a un documento vecchio, ricaricalo.',
      },
      {
        heading: 'Memoria delle risposte',
        text: 'Per 10 minuti una risposta già data viene riusata per una domanda con lo stesso significato. Livelli: Standard (valore di piattaforma), Prudente, Solo domande identiche. Più il livello è prudente, meno risposte vengono riusate. La percentuale di riuso è in RAG Analytics.',
      },
    ],
  },
  {
    id: 'voice',
    title: 'Voce',
    icon: Mic,
    blocks: [
      {
        bullets: [
          'Text-to-Speech: solo per gli annunci IVR del centralino.',
          'Trascrizione (Speech-to-Text) in tre contesti, ognuno con provider e modello propri: transcript testuale degli agenti Realtime, voicemail, video e meeting. Provider ammessi: OpenAI, Gemini e Custom AI (il transcript Realtime solo OpenAI e Gemini).',
          'Gli agenti vocali (OpenAI Realtime, Gemini Live, ElevenLabs) si configurano per agente nella scheda Agenti AI, non qui.',
        ],
      },
    ],
  },
  {
    id: 'other',
    title: 'Analisi e altre funzioni',
    icon: Layers,
    blocks: [
      {
        bullets: [
          'Analisi delle conversazioni: sentiment, riassunto, intenti e momenti chiave dopo la fine di chiamate e chat.',
          'Agenti AI: modello iniziale proposto ai nuovi agenti.',
          'Analisi e riassunto file, Landing Builder (testo e visione), valutazione della qualità delle risposte, trascrizione del workspace personale.',
        ],
      },
    ],
  },
  {
    id: 'catalog',
    title: 'Catalogo, capacità e modelli deprecati',
    icon: Brain,
    blocks: [
      { text: 'Il catalogo elenca i modelli di ogni provider con capacità (chat, embedding, voce, trascrizione), tipi di input (testo, immagini, audio, video), funzioni (strumenti, JSON, visione), finestra di contesto e prezzi. Si aggiorna da solo ogni 6 ore.' },
      {
        heading: 'Capacità richieste',
        text: 'Ogni funzione dichiara cosa le serve. Selettori, consigli e configurazione consigliata mostrano solo i modelli che lo soddisfano, e il salvataggio rifiuta un modello non adatto.',
      },
      {
        heading: 'Modelli deprecati',
        bullets: [
          'In dismissione: il provider ha annunciato il ritiro, il modello funziona ancora.',
          'Non disponibile: superata la data di spegnimento, oppure assente da 2 aggiornamenti completi del catalogo.',
          'I modelli in dismissione o non disponibili escono da selettori e consigli; la configurazione consigliata si riallinea da sola. Le scelte fatte a mano non vengono cambiate: vanno aggiornate da qui.',
        ],
      },
      {
        heading: 'Custom AI nel catalogo',
        text: 'I modelli dei tuoi endpoint Custom AI compaiono solo nel tuo tenant. Ogni 6 ore si rilegge l\'elenco dei modelli di ogni endpoint: un modello assente da 2 aggiornamenti diventa "Non più offerto" ed esce dal routing finché l\'endpoint non lo rielenca.',
      },
    ],
  },
  {
    id: 'scope',
    title: 'Cosa vale per il tenant e cosa per la piattaforma',
    icon: Building2,
    blocks: [
      {
        heading: 'Per il tuo tenant',
        bullets: [
          'Chiavi dei provider ed endpoint Custom AI.',
          'Embedder e modello chat della pipeline RAG.',
          'Provider e modello di ogni funzione, modalità rerank, ricerca avanzata, memoria delle risposte, timeout OCR.',
        ],
      },
      {
        heading: 'Per tutta la piattaforma (super admin)',
        bullets: [
          'Default delle funzioni non scelte dal tenant.',
          'Catalogo dei modelli nativi e suo aggiornamento.',
          'Parametri tecnici della ricerca (quantità di passaggi, contesto dei frammenti) e tempo massimo del rerank.',
        ],
      },
    ],
  },
];

export default function RoutingGuideDialog() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>(ROUTING_GUIDE_SECTIONS[0]?.id ?? 'overview');
  const section = ROUTING_GUIDE_SECTIONS.find((s) => s.id === active) ?? ROUTING_GUIDE_SECTIONS[0];

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="routing-guide-open">
        <BookOpen className="h-4 w-4 mr-2" />
        Guida
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden" data-testid="routing-guide">
          <DialogHeader className="border-b px-5 py-4 text-left">
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Guida al Routing AI
            </DialogTitle>
            <DialogDescription>Come funziona ogni parte della pagina, dall&apos;embedder alle singole funzioni.</DialogDescription>
          </DialogHeader>
          <div className="flex max-h-[70vh] flex-col md:flex-row">
            <nav
              className="flex shrink-0 gap-1 overflow-x-auto border-b p-2 md:w-60 md:flex-col md:overflow-y-auto md:border-b-0 md:border-r"
              aria-label="Indice della guida"
            >
              {ROUTING_GUIDE_SECTIONS.map((s) => {
                const Icon = s.icon;
                const selected = s.id === section?.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActive(s.id)}
                    aria-current={selected ? 'true' : undefined}
                    data-testid={`routing-guide-nav-${s.id}`}
                    className={`flex shrink-0 items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      selected ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-nowrap md:whitespace-normal">{s.title}</span>
                  </button>
                );
              })}
            </nav>
            {section && (
              <article className="min-w-0 flex-1 space-y-4 overflow-y-auto p-5" data-testid={`routing-guide-section-${section.id}`}>
                <h3 className="text-base font-semibold text-foreground">{section.title}</h3>
                {section.blocks.map((b, i) => (
                  <div key={`${section.id}-${i}`} className="space-y-1.5">
                    {b.heading && <h4 className="text-sm font-semibold text-foreground">{b.heading}</h4>}
                    {b.text && <p className="text-sm leading-relaxed text-muted-foreground">{b.text}</p>}
                    {b.bullets && (
                      <ul className="ml-5 list-disc space-y-1 text-sm leading-relaxed text-muted-foreground">
                        {b.bullets.map((t) => <li key={t}>{t}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </article>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
